<template>

  <div v-if="loaded">
    <v-subheader>Total {{expenseCount | numformat('', 0)}} Item(s): <b>&nbsp;{{totalExpense | numformat('Rp. ', 0)}}</b></v-subheader>
    <v-tabs>
      <template v-for="(value, index) in expensesByType">
        <v-tab ripple :key="index" :href="`#${value.category}`">
        {{value.category}}<v-chip>{{value.items.length}}</v-chip>
        </v-tab>
        <v-tab-item :key="`expense-` + index" :id="`${value.category}`">
          <v-card>
            <v-card-text>
              Total: <strong>{{value.items | expensesSum | numformat('Rp. ', 0)}}</strong>
            </v-card-text>
          </v-card>
        </v-tab-item>
        <v-tab-item :key="index" :id="`${value.category}`">
          <v-list v-for="expense in value.items" :key="expense.id">
            <expense-card :value="expense" @delete="_delete" @edit="edit" :editable="editable" />
          </v-list>
        </v-tab-item>
      </template>
      <h2 v-show="expensesByType.length === 0">No Expenses</h2>
    </v-tabs>
  </div>
  <v-card v-else>
    <v-card-text>
      <h2>Loading...</h2>
    </v-card-text>
  </v-card>
</template>

<script>
import Promise from 'promise-polyfill'
import ExpenseCard from './ExpenseCard'
import _ from 'lodash'
import Vuex from 'vuex'

const btRequestStore = Vuex.createNamespacedHelpers('BusinessTripRequest/Request')
const btRequestEmployeesStore = Vuex.createNamespacedHelpers('BusinessTripRequest/Employees')
const btRequestExpensesStore = Vuex.createNamespacedHelpers('BusinessTripRequest/Expenses')
const dialogsStore = Vuex.createNamespacedHelpers('Dialogs')

export default {
  name: 'business-trip-request-expense',
  components: {
    ExpenseCard
  },
  props: {
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    expenseNewRoute: {
      type: [String, Object],
      required: true
    },
    expenseDetailRoute: {
      type: [String, Object],
      required: true
    },
    backRoute: {
      type: [String, Object],
      required: true
    }
  },
  data () {
    return {
      employee: {},
      expenses: [],
      loaded: false,
      buttons: [
        {label: 'Close', icon: 'exit_to_app', tooltip: 'Close', handler: this.close},
        {label: 'New Expense',
          icon: 'add',
          tooltip: 'New Expense',
          to: {
            name: this.expenseNewRoute,
            params: {
              id: this.$route.params.id,
              empid: this.$route.params.empid,
              backRoute: {
                name: this.$route.name,
                params: {id: this.$route.params.id,
                  empid: this.$route.params.empid,
                  editable: this.editable,
                  backRoute: this.backRoute,
                  expenseDetailRoute: this.expenseDetailRoute,
                  expenseNewRoute: this.expenseNewRoute
                }
              }
            }
          },
          render: this.editable},
        {label: 'Generate Expense', icon: 'build', tooltip: 'Generate Expense', handler: this.genExpense, render: this.editable},
        {label: 'Refresh', icon: 'refresh', tooltip: 'Refresh', handler: this.refresh}
      ]
    }
  },
  computed: {
    title () {
      if (this.loaded) {
        return 'Business Trip Request Expenses: ' + this.employee.employee.name + ' (' + this.employee.employee.nik + ')'
      } else {
        return 'Business Trip Request Expenses'
      }
    },
    expensesByType () {
      const categories = []

      this.expenses.forEach(x => {
        const proper = x.tripExpenseType.replace('_', ' ')
        let category = categories.find(x => x.category === proper)
        if (typeof category === 'undefined') {
          category = {category: proper, items: []}
          categories.push(category)
        }
        category.items.push(x)
      })
      return _.sortBy(categories, x => x.category)
    },
    totalExpense () {
      return this.expenses.map(x => x.subTotal).reduce((x, y) => x + y, 0)
    },
    expenseCount () {
      return this.expenses.length
    },
    containsGeneratedExpenses () {
      const expenseTypes = ['ACCOMODATION', 'MEAL_ALLOWANCE', 'POCKET_ALLOWANCE']
      return this.expenses.filter(x => expenseTypes.includes(x.tripExpenseType)).length > 0
    }
  },
  methods: {
    ...btRequestStore.mapActions({
      getRequest: 'getRequest'
    }),
    ...btRequestEmployeesStore.mapActions({
      getEmployees: 'getEmployees',
      getEmployeeById: 'getEmployeeById'
    }),
    ...btRequestExpensesStore.mapActions({
      getExpenses: 'getExpenses',
      deleteExpense: 'deleteExpense',
      generateExpense: 'generateExpense'
    }),
    ...dialogsStore.mapActions({
      showConfirmation: 'showConfirmation',
      closeConfirmation: 'closeConfirmation'
    }),
    loadResources () {
      const self = this

      self.loaded = false

      const resources = [self.getRequest({vueinstance: self, id: self.$route.params.id}),
        self.getEmployees({vueinstance: self, id: self.$route.params.id})]

      Promise.all(resources)
        .then(res => {
          self.getEmployeeById({vueinstance: self, id: self.$route.params.id, empid: self.$route.params.empid})
            .then(res1 => {
              self.employee = _.cloneDeep(res1)
              self.getExpenses({vueinstance: self, id: self.$route.params.id, empid: self.$route.params.empid})
                .then(res2 => {
                  self.expenses = _.sortBy(_.cloneDeep(res2), x => x.tripDate)
                  self.loaded = true
                  self._configureToolbar()
                })
            })
        })
        .catch(err => {
          self.$error(err)
        })
    },
    _configureToolbar () {
      this.$store.dispatch('setTitle', {title: this.title})
      this.$store.dispatch('Action/setActions', this.buttons)
    },
    close () {
      this.$router.replace({name: this.backRoute, params: {id: this.$route.params.id, editable: this.editable}})
    },
    refresh () {
      this.loadResources()
    },
    _delete (expense) {
      const self = this
      const title = self.$options.filters.moment(expense.tripDate, 'LL') + ' ' + expense.category.name + ' ' + expense.total

      const yesHandler = () => {
        self.deleteExpense({vueinstance: self, id: self.$route.params.id, empid: self.$route.params.empid, expid: expense.id})
          .then(res => {
            self.$error('Business Trip Request Expense ' + title + ' deleted')
            self.closeConfirmation()
            self.refresh()
          })
          .catch(err => self.$error(err))
      }

      const noHandler = () => {
        self.closeConfirmation()
      }

      self.showConfirmation({
        message: 'Are you sure?',
        title: 'Delete Business Trip Request Expense ' + title,
        buttons: [{label: 'Yes', handler: yesHandler}, {label: 'No', handler: noHandler}]
      })
    },
    edit (expense) {
      this.$router.replace({name: this.expenseDetailRoute,
        params: {id: this.$route.params.id,
          empid: this.$route.params.empid,
          expid: expense.id,
          editable: this.editable && expense.editable,
          backRoute:
            {
              name: this.$route.name,
              params: {id: this.$route.params.id,
                empid: this.$route.params.empid,
                editable: this.editable,
                backRoute: this.backRoute,
                expenseDetailRoute: this.expenseDetailRoute,
                expenseNewRoute: this.expenseNewRoute
              }
            }}})
    },
    genExpense () {
      const self = this

      const yesHandler = () => {
        self.generateExpense({vueinstance: self, id: this.$route.params.id, empid: this.$route.params.empid})
          .then(res => {
            self.refresh()
            self.closeConfirmation()
          }).catch(err => {
            self.$error(err)
            self.closeConfirmation()
          })
      }

      const noHandler = () => { self.closeConfirmation() }

      self.showConfirmation({
        message: (self.containsGeneratedExpenses
          ? '<h1>The existing Accomodation, Meal and Pocket Allowance Expenses will be deleted!</h1>' : '') +
        '<h2>Are you sure?</h2>',
        title: 'Generating expense(s) for this business trip request.',
        buttons: [{label: 'Yes', handler: yesHandler}, {label: 'No', handler: noHandler}]
      })
    }
  },
  filters: {
    expensesSum (expenses) {
      return expenses.map(x => x.subTotal).reduce((x, y) => x + y, 0)
    }
  },
  mounted () {
    if (!this.loaded) {
      this.loadResources()
    }
  },
  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.loadResources()
    })
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/business-trip/BusinessTripRequestExpense.vue